import {
  buildTaskColumnApiField,
  parseTaskColumnPath,
  type ColumnConfigEntry,
  type SystemEntity,
} from '@erp/shared-types';
import type { TaskAnchorFieldCatalogEntry } from '@erp/rpc';
import {
  buildPickedColumn,
  humanizeColumnPath,
} from '../../../tasks/columns/taskColumnOptions';

/**
 * Pure planning for the task-page assistant tools: which column to show or
 * hide, and which `?ref` pick a reference filter sets. Kept free of React so
 * the rules (catalog-only paths, locked columns, filter needs its column) are
 * unit-testable. Errors are sentences the model can act on.
 */

/** Paths listed in an error or the prompt context. */
const MAX_LISTED_PATHS = 60;

export type ShowColumnPlan =
  | { kind: 'already_visible'; columnId: string; header: string }
  | { kind: 'unhide'; columnId: string; header: string }
  | { kind: 'add'; column: Omit<ColumnConfigEntry, 'id'> };

export interface TaskPageColumnState {
  /** The queue's columns, hidden ones included. */
  columns: readonly ColumnConfigEntry[];
  /** The anchor field catalog; undefined while loading. */
  catalog: readonly TaskAnchorFieldCatalogEntry[] | undefined;
  /** False when the page locks its columns to the admin defaults. */
  canCustomizeColumns: boolean;
}

/**
 * Header for a picked anchor field: a reference column drops its id suffix
 * (`supplierId` → "Supplier"), anything else is the humanized last segment.
 */
export function defaultAnchorColumnHeader(
  path: string,
  references: SystemEntity | undefined,
): string {
  if (references) {
    const stripped = path.replace(/(_id|Id)$/, '');
    if (stripped && stripped !== path) return humanizeColumnPath(stripped);
  }
  return humanizeColumnPath(path);
}

function listPaths(catalog: readonly TaskAnchorFieldCatalogEntry[]): string {
  const paths = catalog.slice(0, MAX_LISTED_PATHS).map((entry) => entry.path);
  return paths.join(', ');
}

function requireCatalog(
  catalog: TaskPageColumnState['catalog'],
): readonly TaskAnchorFieldCatalogEntry[] {
  if (!catalog) {
    throw new Error(
      "The page has not loaded the record's fields yet. Ask the user to retry in a moment.",
    );
  }
  return catalog;
}

function catalogEntry(
  catalog: readonly TaskAnchorFieldCatalogEntry[],
  path: string,
): TaskAnchorFieldCatalogEntry {
  const trimmed = path.trim().replace(/^anchor\./, '');
  const entry =
    catalog.find((field) => field.path === trimmed) ??
    catalog.find((field) => field.path.toLowerCase() === trimmed.toLowerCase());
  if (!entry) {
    throw new Error(
      `"${path}" is not a field of the record these tasks work on. Available fields: ${listPaths(catalog)}.`,
    );
  }
  return entry;
}

function existingAnchorColumn(
  columns: readonly ColumnConfigEntry[],
  path: string,
): ColumnConfigEntry | undefined {
  const apiField = buildTaskColumnApiField('anchor', path);
  return columns.find((column) => column.apiField === apiField);
}

const LOCKED_COLUMNS_MESSAGE =
  "This page's columns are locked by an admin, so they cannot be changed here. An admin can add the column to the page's default columns in Settings → Task pages.";

/** Show one anchor field as a column: already there, unhide it, or add it. */
export function planShowAnchorColumn(
  state: TaskPageColumnState,
  input: { path: string; header?: string },
): ShowColumnPlan {
  const entry = catalogEntry(requireCatalog(state.catalog), input.path);
  const existing = existingAnchorColumn(state.columns, entry.path);
  if (existing && !existing.hidden) {
    return {
      kind: 'already_visible',
      columnId: existing.id,
      header: existing.header,
    };
  }
  if (!state.canCustomizeColumns) throw new Error(LOCKED_COLUMNS_MESSAGE);
  if (existing) {
    return { kind: 'unhide', columnId: existing.id, header: existing.header };
  }
  return {
    kind: 'add',
    column: buildPickedColumn({
      source: 'anchor',
      path: entry.path,
      header:
        input.header?.trim() ||
        defaultAnchorColumnHeader(entry.path, entry.references),
    }),
  };
}

/** Hide a visible column, found by id or (case-insensitive) header. */
export function planHideColumn(
  state: Pick<TaskPageColumnState, 'columns' | 'canCustomizeColumns'>,
  column: string,
): { columnId: string; header: string } {
  if (!state.canCustomizeColumns) throw new Error(LOCKED_COLUMNS_MESSAGE);
  const wanted = column.trim().toLowerCase();
  const visible = state.columns.filter((entry) => !entry.hidden);
  const match =
    visible.find((entry) => entry.id === column.trim()) ??
    visible.find((entry) => entry.header.trim().toLowerCase() === wanted);
  if (!match) {
    throw new Error(
      `No visible column "${column}". Visible columns: ${visible
        .map((entry) => entry.header)
        .join(', ')}.`,
    );
  }
  return { columnId: match.id, header: match.header };
}

export interface ReferenceFilterPlan {
  /** How the filter's column becomes visible; null when clearing. */
  showColumn: ShowColumnPlan | null;
  /** The next `?ref` value. */
  ref: Record<string, string> | undefined;
  path: string;
  entityType: SystemEntity;
}

/**
 * Filter the queue to tasks whose record points at `recordId` through the
 * reference field `path` (null clears it). The toolbar only applies a pick
 * whose column is visible, so the plan shows that column too.
 */
export function planReferenceFilter(
  state: TaskPageColumnState,
  currentRef: Readonly<Record<string, string>> | undefined,
  input: { path: string; recordId: string | null },
): ReferenceFilterPlan {
  const catalog = requireCatalog(state.catalog);
  const entry = catalogEntry(catalog, input.path);
  const entityType = entry.references;
  if (!entityType) {
    const references = catalog.filter((field) => field.references);
    throw new Error(
      `"${entry.path}" does not point at another record, so it cannot be filtered on. Fields that can: ${
        references.map((field) => field.path).join(', ') || 'none'
      }.`,
    );
  }
  const { [entry.path]: _previous, ...rest } = currentRef ?? {};
  const next = input.recordId
    ? { ...rest, [entry.path]: input.recordId }
    : rest;
  return {
    showColumn: input.recordId
      ? planShowAnchorColumn(state, { path: entry.path })
      : null,
    ref: Object.keys(next).length > 0 ? next : undefined,
    path: entry.path,
    entityType,
  };
}

export interface TaskPageColumnContext {
  header: string;
  /** `anchor.supplierId`, `input.amount`, … ; empty for built-in columns. */
  field?: string;
  hidden?: true;
}

/** The columns as the prompt sees them (header + data path). */
export function columnsContext(
  columns: readonly ColumnConfigEntry[],
): TaskPageColumnContext[] {
  return columns.slice(0, MAX_LISTED_PATHS).map((column) => {
    const parsed = parseTaskColumnPath(column.apiField);
    return {
      header: column.header,
      ...(parsed
        ? { field: `${parsed.source}.${parsed.segments.join('.')}` }
        : {}),
      ...(column.hidden ? { hidden: true as const } : {}),
    };
  });
}

/** Anchor fields offered to the model: references first, capped. */
export function anchorFieldsContext(
  catalog: readonly TaskAnchorFieldCatalogEntry[] | undefined,
): Array<{ path: string; references?: SystemEntity }> {
  const entries = [...(catalog ?? [])].sort(
    (a, b) => Number(Boolean(b.references)) - Number(Boolean(a.references)),
  );
  return entries.slice(0, MAX_LISTED_PATHS).map((entry) => ({
    path: entry.path,
    ...(entry.references ? { references: entry.references } : {}),
  }));
}
